// Pokemon TCG Types for Master Set tracking

export interface PokemonSet {
  id: string;
  name: string;
  series: string;
  printedTotal: number;
  totalCards: number; // Total cards including secret rares
  releaseDate: string;
  images: {
    symbol?: string;
    logo: string;
  };
  era: string;
  valueTier: string;
  owned: number;
  completion: number;
}

export interface PokemonCard {
  id: string;
  baseCardId?: string;
  name: string;
  supertype: string;
  subtypes: string[];
  number: string;
  rarity: string;
  types?: string[];
  isChaseCard?: boolean; // Computed based on rarity
  set: {
    id: string;
    name: string;
    series: string;
    printedTotal: number;
    releaseDate: string;
  };
  images: {
    small: string;
    large: string;
  };
  tcgplayer?: {
    url: string;
    updatedAt: string;
    prices?: {
      [key: string]: {
        low?: number;
        mid?: number;
        high?: number;
        market?: number;
      };
    };
  };
  justtcgId?: string;
}

// Chase card rarities for Pokémon TCG (secret rares and above)
export const POKEMON_CHASE_RARITIES = [
  'Illustration Rare',          // IR - full-art alternate illustration
  'Special Illustration Rare',  // SIR - full-bleed alternate art
  'Hyper Rare',                 // Gold card
  'Ultra Rare',                 // ex, V, VMAX full-art
  'Double Rare',                // Two-star pull
  'MEGA_ATTACK_RARE',           // Custom rarity for Mega Evolution set
  'Mega Hyper Rare',            // Custom top rarity for Mega Evolution set
  'Shiny Rare',                 // Shiny vault cards
  'Shiny Ultra Rare',           // Shiny vault ultra rare
  'Rainbow Rare',               // Rainbow holo rare
  'Gold Rare',                  // Gold card
  'Secret Rare',                // Secret rare
  'Amazing Rare',               // Amazing rare (SwSh era)
  'Radiant Rare',               // Radiant rare
];

export function isPokemonChaseCard(card: { rarity?: string; number?: string; set?: { printedTotal?: number } }): boolean {
  const rarity = card.rarity?.trim() || '';
  if (POKEMON_CHASE_RARITIES.includes(rarity)) return true;

  // Secret rares: card number exceeds the set's printedTotal
  const cardNum = parseInt(card.number || '0', 10);
  const printedTotal = card.set?.printedTotal || 0;
  if (!isNaN(cardNum) && printedTotal > 0 && cardNum > printedTotal) return true;

  return false;
}

export const POKEMON_RARITY_WEIGHTS: Record<string, number> = {
  'Mega Hyper Rare': 100,
  'Special Illustration Rare': 95,
  'Hyper Rare': 90,
  'Rainbow Rare': 85,
  'Gold Rare': 80,
  'Secret Rare': 75,
  'Illustration Rare': 70,
  'Shiny Ultra Rare': 65,
  'Shiny Rare': 60,
  'Ultra Rare': 55,
  'Radiant Rare': 50,
  'Amazing Rare': 45,
  'Double Rare': 40,
  'MEGA_ATTACK_RARE': 35,
  'Holo Rare': 30,
  'Rare Holo': 30,
  'Rare Holo V': 30,
  'Rare Holo VMAX': 30,
  'Rare Holo VSTAR': 30,
  'Rare Holo EX': 30,
  'Rare Holo GX': 30,
  'Rare': 25,
  'Uncommon': 20,
  'Common': 10,
};

export function getPokemonRarityWeight(rarity: string | undefined): number {
  if (!rarity) return 0;
  return POKEMON_RARITY_WEIGHTS[rarity.trim()] || 5; // Default lower than common if unknown
}

export interface SetProgress {
  setId: string;
  owned: number;
  total: number;
  completion: number;
  missingCards: string[];
  lastUpdated: string;
}

export interface BulkAddRequest {
  setId: string;
  cards: {
    cardId: string;
    quantity: number;
    condition: string;
    purchasePrice: number;
  }[];
  totalCost: number;
  purchaseDate: string;
  source: string;
}
